import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, IMemberSearchFilters, MemberType } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CD from 'app/cd/models/essette-api';

interface HistoryDetail {
    description?: string | null;
    type?: string | null;
    dateRange?: CD.DateRange;
}

@autoinject()
export class MemberEligibilityDetail extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public planId: string | null = null;

    @bindable.string({ defaultBindingMode: bindingMode.toView })
    protected memberType: MemberType = MemberType.Member;

    protected details: HistoryDetail[] = [];
    protected memberAidCodes: CD.MemberAidCode[] = [];
    protected memberConditions: CD.MemberCondition[] = [];

    protected searchFilters: IMemberSearchFilters = {};
    private sorting: Sorting = new Sorting();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind() {
        this.reload();
    }


    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadHistoryDetails();
    }

    protected applySort(event: SortChangeEvent, details: (CD.MemberAidCode | CD.MemberCondition)[]): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(details, sorting);
    }

    ////****************** Functions ************************************************////
    protected consolidateDetails(): void {
        this.memberAidCodes.forEach(aidCode => {
            this.details.push({
                description: aidCode.aidCode?.$display,
                type: this.i18n.tr('cd:aidCode.$short'),
                dateRange: aidCode.dateRange
            })
        });

        this.memberConditions.forEach(condition => {
            this.details.push({
                description: condition.condition?.$display,
                type: this.i18n.tr('cd:condition.$'),
                dateRange: condition.dateRange
            });
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHistoryDetails(): Promise<void> {
        if(!this.planId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const aidCodeIncludes = [
            'aidCode'
        ];

        const aidCodeFields = [
            'aidCode.description',
            'dateRange.start'
        ];

        const conditionIncludes = [
            'condition'
        ];

        const conditionFields = [
            'condition.description',
            'dateRange.start'
        ];

        this.searchFilters = {
            currentHistory: true
        };

        try {
            this.state.beginLoad();

            const [aidCodeResponse, conditionResponse] = await Promise.all(
                [
                    this.service.loadMemberAidCodesByPlan(this.planId, aidCodeIncludes, this.searchFilters, options, aidCodeFields),
                    this.memberType === MemberType.Patient ? this.service.loadPatientConditionsByPlan(this.planId, conditionIncludes, this.searchFilters, options, conditionFields) : this.service.loadMemberConditionsByPlan(this.planId, conditionIncludes, this.searchFilters, options, conditionFields)
                ]
            );

            this.memberAidCodes = aidCodeResponse.results;
            this.memberConditions = conditionResponse.results;
            this.consolidateDetails();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
